import { Star, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { SITE } from "@/config/site";
import { getGoogleReviews } from "@/lib/google-reviews.functions";

const PAGE_SIZE = 5;
const AUTOPLAY_MS = 6000;

export function GoogleReviews() {
  const fetchReviews = useServerFn(getGoogleReviews);
  const { data } = useQuery({
    queryKey: ["google-reviews"],
    queryFn: () => fetchReviews(),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60 * 10, // poll every 10 minutes
  });


  const reviews = data?.reviews ?? [];
  const avgRating = data?.avgRating ?? 5;
  // Known Google profile count — shown until the live fetch resolves so the
  // section never renders a "0 reviews" state on first paint.
  const totalRatings = data?.totalRatings ?? 11;

  const pageCount = Math.max(1, Math.ceil(reviews.length / PAGE_SIZE));
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (page >= pageCount) setPage(0);
  }, [pageCount, page]);

  useEffect(() => {
    if (paused || pageCount <= 1) return;
    const id = setInterval(() => setPage((p) => (p + 1) % pageCount), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, pageCount]);

  const visible = reviews.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <section className="bg-cream/85 backdrop-blur-sm py-20 md:py-28">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="eyebrow">What our customers say</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl">
              Rated <span className="rainbow-text">5 stars</span> on Google.
            </h2>
            <p className="mt-4 text-muted-foreground">
              {totalRatings} real reviews from real customers across Tidworth, Andover and Salisbury.
            </p>
          </div>
          <div className="rounded-3xl border border-border bg-card px-6 py-5 shadow-soft flex items-center gap-4 shrink-0">
            <div className="text-center">
              <div className="font-display text-3xl text-primary leading-none">
                {avgRating.toFixed(1)}
              </div>
              <div className="mt-1 flex items-center gap-0.5 justify-center">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" />
                ))}
              </div>
            </div>
            <div className="text-sm">
              <div className="font-medium text-ink">{totalRatings} Google Reviews</div>
              <a
                href={SITE.googleBusinessUrl}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1 mt-0.5"
              >
                See all on Google <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>

        <div
          className="mt-10 md:mt-12"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="grid gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {visible.map((r) => (
              <article
                key={r.name + r.publishedAt}
                className="rounded-2xl md:rounded-3xl border border-border bg-card p-5 md:p-6 flex flex-col animate-fade-in"
              >
                <div className="flex items-center gap-0.5 shrink-0">
                  {[...Array(r.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>
                <p className="mt-3 md:mt-4 text-sm md:text-[15px] text-foreground/90 leading-relaxed flex-1">
                  "{r.text}"
                </p>
                <div className="mt-4 md:mt-5 pt-3 md:pt-4 border-t border-border">
                  <div className="font-medium text-sm text-ink">{r.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{r.date}</div>
                </div>
              </article>
            ))}
          </div>

          {pageCount > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2" aria-label="Reviews pagination">
              {Array.from({ length: pageCount }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i)}
                  aria-label={`Show reviews page ${i + 1}`}
                  aria-current={i === page}
                  className={`h-2.5 rounded-full transition-all ${
                    i === page ? "w-8 bg-primary" : "w-2.5 bg-border hover:bg-muted-foreground/50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={SITE.googleBusinessUrl}
            target="_blank"
            rel="noreferrer"
            className="btn-outline"
          >
            Read more reviews on Google <ExternalLink className="h-4 w-4" />
          </a>
          <a
            href={SITE.googleReviewUrl}
            target="_blank"
            rel="noreferrer"
            className="btn-primary"
          >
            Leave us a Google review <ExternalLink className="h-4 w-4" />
          </a>

        </div>
      </div>
    </section>
  );
}
